import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound, CheckCircle2 } from "lucide-react";

/**
 * Profile page — currently exposes the 2FA self-service flow.
 *
 * Three states of the 2FA card:
 *   • totp_enabled = false, no setup in progress → primary CTA "Activer la 2FA"
 *   • setup in progress (qrData stored locally) → QR + manual key + 6-digit input
 *   • totp_enabled = true → confirmation card + "Désactiver" form
 */
export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, qr_data_uri }
  const [verifyCode, setVerifyCode] = useState("");
  const [busy, setBusy] = useState(false);

  // disable form
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  if (!user) return null;

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup", {});
      setSetupData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'initialiser la 2FA");
    } finally {
      setBusy(false);
    }
  };

  const cancelSetup = () => {
    setSetupData(null);
    setVerifyCode("");
  };

  const confirmSetup = async () => {
    if (verifyCode.length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify-setup", { code: verifyCode });
      await refreshUser();
      setSetupData(null);
      setVerifyCode("");
      toast.success("Authentification à deux facteurs activée.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
    } finally {
      setBusy(false);
    }
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePw, code: disableCode || null });
      await refreshUser();
      setDisablePw("");
      setDisableCode("");
      toast.success("Authentification à deux facteurs désactivée.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la désactivation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 py-6 sm:py-12" data-testid="profile-page">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Profil et sécurité</h1>
      <p className="text-sm text-zinc-500 mt-1">
        Connecté·e en tant que <span className="font-mono">{user.email}</span>
      </p>

      {/* 2FA card */}
      <div className="mt-8 border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="profile-2fa-card">
        <div className="px-5 py-4 border-b border-zinc-100 flex items-start gap-3">
          <div className={`h-10 w-10 rounded-sm flex items-center justify-center shrink-0 ${user.totp_enabled ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
            {user.totp_enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-zinc-900">Authentification à deux facteurs (2FA)</h2>
            <p className="text-xs text-zinc-600 mt-0.5">
              {user.totp_enabled
                ? "Active. Un code à 6 chiffres vous est demandé à chaque connexion."
                : "Renforcez la sécurité de votre compte en exigeant un code à 6 chiffres généré par votre téléphone à chaque connexion."}
            </p>
          </div>
          {user.totp_enabled && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700">
              <CheckCircle2 className="h-2.5 w-2.5" /> Activée
            </span>
          )}
        </div>

        <div className="p-5">
          {!user.totp_enabled && !setupData && (
            <div>
              <p className="text-sm text-zinc-700">
                Vous aurez besoin d'une application d'authentification :
                <span className="text-zinc-500"> Google Authenticator, Authy, Microsoft Authenticator, 1Password…</span>
              </p>
              <Button
                onClick={startSetup}
                disabled={busy}
                className="mt-4 bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
                data-testid="profile-2fa-start-btn"
              >
                <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                Activer la 2FA
              </Button>
            </div>
          )}

          {!user.totp_enabled && setupData && (
            <div className="space-y-4" data-testid="profile-2fa-setup">
              <ol className="list-decimal list-inside text-sm text-zinc-700 space-y-1">
                <li>Ouvrez votre application d'authentification.</li>
                <li>Scannez ce QR code <span className="text-zinc-500">(ou saisissez la clé manuelle ci-dessous)</span>.</li>
                <li>Saisissez le code à 6 chiffres affiché par l'application pour finaliser l'activation.</li>
              </ol>
              <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-start bg-zinc-50/60 border border-zinc-200 rounded-sm p-4">
                <img
                  src={setupData.qr_data_uri}
                  alt="QR code 2FA"
                  className="h-44 w-44 border border-zinc-200 bg-white p-2 rounded-sm"
                  data-testid="profile-2fa-qr"
                />
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
                    <KeyRound className="h-3 w-3 inline mr-1 align-[-2px]" />
                    Clé manuelle (si scan impossible)
                  </Label>
                  <div className="mt-1 font-mono text-xs bg-white border border-zinc-300 rounded-sm px-3 py-2 break-all select-all">
                    {setupData.secret}
                  </div>
                  <Label className="mt-4 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 block">
                    Code à 6 chiffres généré
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                    className="mt-1 rounded-sm font-mono text-xl tracking-[0.4em] text-center"
                    placeholder="••••••"
                    autoFocus
                    data-testid="profile-2fa-verify-input"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      onClick={confirmSetup}
                      disabled={busy || verifyCode.length !== 6}
                      className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
                      data-testid="profile-2fa-confirm-btn"
                    >
                      {busy ? "Vérification..." : "Activer la 2FA"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={cancelSetup}
                      disabled={busy}
                      className="rounded-sm border-zinc-200"
                      data-testid="profile-2fa-cancel-btn"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {user.totp_enabled && (
            <form onSubmit={disable2fa} className="space-y-3" data-testid="profile-2fa-disable">
              <p className="text-sm text-zinc-700">
                Pour désactiver la 2FA, confirmez votre mot de passe et saisissez un code TOTP valide.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Mot de passe</Label>
                  <Input
                    type="password"
                    required
                    value={disablePw}
                    onChange={(e) => setDisablePw(e.target.value)}
                    className="mt-1 rounded-sm"
                    data-testid="profile-2fa-disable-password"
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Code à 6 chiffres</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                    className="mt-1 rounded-sm font-mono tracking-[0.3em]"
                    placeholder="••••••"
                    data-testid="profile-2fa-disable-code"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                variant="outline"
                className="rounded-sm border-zinc-300 text-zinc-700"
                data-testid="profile-2fa-disable-btn"
              >
                <ShieldOff className="h-3.5 w-3.5 mr-1.5" />
                Désactiver la 2FA
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
